import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CHAPTER_STATE_EVENT, OPEN_CHAPTER_EVENT } from './ChapterFold';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { directionsUrl } from '../utils/placeFormat';
import { getAttractionsByNeighborhood } from '../utils/neighborhoodHelpers';
import { MapArt, MapPin } from './Illustrations';

// "Weekend picks" — the Capitol Hill attractions, listed under Around here.
// Approximate distance from the house; tapping one shows it in Explore Seattle.
const PICK_DISTANCE: Record<string, string> = {
  'Elliott Bay Book Company': '14 min walk',
  'Chophouse Row': '14 min walk',
  'Cal Anderson Park': '15 min walk',
  'Volunteer Park': '1.8 mi',
  'Washington Park Arboretum': '2.5 mi',
  'Madison Park Beach': '3.0 mi',
};
const PICK_KIND: Record<string, string> = {
  'Elliott Bay Book Company': 'Bookstore',
  'Chophouse Row': 'Shops & food hall',
  'Cal Anderson Park': 'Park',
  'Volunteer Park': 'Water tower views',
  'Washington Park Arboretum': 'Japanese Garden',
  'Madison Park Beach': 'Lake beach',
};
const hillPicks = getAttractionsByNeighborhood('capitol-hill');

// "Around here": the neighborhood directory. The house becomes the home-base
// photo on the left; the closest everyday spots are grouped on the right.
// Links open directions from the guest's own location — the house address
// never appears on the public page.

type Spot = {
  icon: string;
  kind: string;
  name: string;
  value: string;
  unit: 'min' | 'mi';
  address?: string;
  mode?: 'walking' | 'transit';
  // When the place is already featured further down the page, tapping it
  // scrolls there instead of leaving for Google Maps.
  restaurant?: string;
  attraction?: string;
};

type Group = { title: string; spots: Spot[] };

const groups: Group[] = [
  {
    title: 'Eat & drink',
    spots: [
      { icon: '🍸', kind: 'Cocktails', restaurant: 'Canon', name: 'Canon', value: '2', unit: 'min', address: '928 12th Ave, Seattle, WA 98122' },
      { icon: '☕', kind: 'Coffee', restaurant: 'Anchorhead Coffee', name: 'Anchorhead Coffee', value: '6', unit: 'min', address: '115 12th Ave, Seattle, WA 98122' },
      { icon: '🍜', kind: 'Thai', restaurant: 'Bai Tong', name: 'Bai Tong', value: '6', unit: 'min', address: '1121 E Pike St, Seattle, WA 98122' },
      { icon: '🍗', kind: 'Quick bite', restaurant: "Dave's Hot Chicken", name: "Dave's Hot Chicken", value: '6', unit: 'min', address: '1200 E Pike St, Seattle, WA 98122' },
      { icon: '🧁', kind: 'Dessert', restaurant: 'Cupcake Royale', name: 'Cupcake Royale', value: '7', unit: 'min', address: '1102 E Pike St, Seattle, WA 98122' },
    ],
  },
  {
    title: 'Daily needs',
    spots: [
      { icon: '🌳', kind: 'Playground & park', name: 'Spring Street Park', value: '4', unit: 'min', address: 'Spring Street Mini Park, Seattle, WA 98122' },
      { icon: '🎓', kind: 'Campus walks', name: 'Seattle University', value: '6', unit: 'min', address: 'Seattle University, Seattle, WA 98122' },
      { icon: '🛒', kind: 'Groceries', name: "Trader Joe's", value: '8', unit: 'min', address: '1700 E Madison St, Seattle, WA 98122' },
    ],
  },
  {
    title: 'Getting around',
    spots: [
      { icon: '🚌', kind: 'Bus · RapidRide G', name: 'E Madison & 14th', value: '2', unit: 'min', address: 'E Madison St & 14th Ave, Seattle, WA 98122' },
      { icon: '🛴', kind: 'Scooters & e-bikes', name: 'Along E Madison & E Pike', value: '3–5', unit: 'min' },
      { icon: '🚇', kind: 'Light rail', name: 'Capitol Hill Station', value: '15', unit: 'min', address: 'Capitol Hill Station, Seattle, WA 98122' },
    ],
  },
];

// Everyday spots (groceries, park, bus, light rail …) for the guide search.
// Eat & drink and the weekend picks are already searchable as restaurants and sights.
export const AROUND_SPOTS = groups.flatMap((g) =>
  g.spots.filter((s) => !s.restaurant && !s.attraction).map((s) => ({ name: s.name, kind: s.kind, value: s.value, unit: s.unit })),
);

// Our Capitol Hill weekend picks, as a list group ordered by distance.
const toSpot = (name: string): Spot | null => {
  const pick = hillPicks.find((p) => p.name === name);
  const dist = PICK_DISTANCE[name];
  if (!pick || !dist) return null;
  const [value, unit] = dist.split(' ');
  return {
    icon: pick.emoji,
    kind: PICK_KIND[name] ?? 'Capitol Hill',
    name,
    value,
    unit: unit === 'mi' ? 'mi' : 'min',
    address: pick.address,
    attraction: name,
  };
};
groups.push({
  title: 'Weekend picks',
  spots: Object.keys(PICK_DISTANCE).map(toSpot).filter((x): x is Spot => x !== null),
});

// Where each place sits on the drawn Around the Hill map (map units, 760 square).
// The Arboretum and Madison Park Beach are past the edge of the map, so they get no dot.
const PINS: Record<string, [number, number]> = {
  Canon: [452, 350],
  'Anchorhead Coffee': [606, 372],
  'Bai Tong': [560, 590],
  "Dave's Hot Chicken": [650, 556],
  'Cupcake Royale': [250, 556],
  'Spring Street Park': [480, 262],
  'Seattle University': [200, 492],
  "Trader Joe's": [700, 470],
  'E Madison & 14th': [486, 400],
  'Along E Madison & E Pike': [338, 222],
  'Capitol Hill Station': [262, 360],
  'Elliott Bay Book Company': [304, 494],
  'Chophouse Row': [420, 640],
  'Cal Anderson Park': [406, 540],
  'Volunteer Park': [590, 190],
};
// Every place gets a number, in list order.
const numberOf = new Map(groups.flatMap((g) => g.spots).map((s, i) => [s.name, i + 1]));
const pins: MapPin[] = groups
  .flatMap((g) => g.spots)
  .filter((s) => PINS[s.name])
  .map((s) => ({ n: numberOf.get(s.name)!, x: PINS[s.name][0], y: PINS[s.name][1] }));

const comforts: { icon: string; label: string; href?: string }[] = [
  { icon: '🚗', label: 'Own parking', href: '#parking' },
  { icon: '🔑', label: 'Keypad entry', href: '#arrival-checkin' },
  { icon: '❄️', label: 'A/C' },
  { icon: '📶', label: 'Wi-Fi', href: '#wifi' },
  { icon: '⛱', label: 'Backyard patio' },
];

const spotLink = (spot: Spot) =>
  spot.address ? `${directionsUrl(spot.name, spot.address)}&travelmode=${spot.mode ?? 'walking'}` : undefined;


type RowProps = { spot: Spot; onLeave: (spot: Spot) => void; onPoint: (n: number | null) => void };

const SpotRow: React.FC<RowProps> = ({ spot, onLeave, onPoint }) => {
  const n = numberOf.get(spot.name)!;
  const onMap = Boolean(PINS[spot.name]);
  const point = { onMouseEnter: () => onPoint(onMap ? n : null), onFocus: () => onPoint(onMap ? n : null) };
  const onPage = onPageSpot(spot);
  const href = onPage ? (spot.restaurant ? '#dining' : '#neighborhoods-explore') : spotLink(spot);
  const content = (
    <>
      <span className={onMap ? 'around-num' : 'around-num is-off-map'} aria-hidden="true">{onMap ? n : '↗'}</span>
      <span className="around-name">
        <small>{spot.kind}</small>
        <strong>{spot.name}</strong>
      </span>
      <span className="around-time">
        {spot.value}
        <small>{spot.unit}</small>
      </span>
    </>
  );
  const label = `${spot.name}, ${spot.kind}, ${spot.unit === 'min' ? `about ${spot.value} minute walk` : `${spot.value} miles away`}`;
  if (onPage) {
    return (
      <li>
        <a
          className="around-row is-on-page"
          href={href}
          data-around-spot={spot.name}
          {...point}
          onClick={(e) => { e.preventDefault(); onLeave(spot); showOnPage(spot); }}
          aria-label={`${label} — see it in ${spot.restaurant ? 'Restaurants' : 'Explore Seattle'}`}
        >
          {content}
        </a>
      </li>
    );
  }
  return (
    <li>
      {href ? (
        <a className="around-row" href={href} target="_blank" rel="noopener noreferrer" data-around-spot={spot.name} aria-label={`${label} — open directions`} {...point}>
          {content}
        </a>
      ) : (
        <div className="around-row" data-around-spot={spot.name} aria-label={label} onMouseEnter={point.onMouseEnter}>{content}</div>
      )}
    </li>
  );
};

// After a guest jumps from "Around here" to a restaurant or sight further down
// the page, a bar pinned to the top of the screen says where they came from,
// takes them back to that row, and steps through the rest of that group's
// places (Canon → Anchorhead Coffee → …). It goes away once they're back
// (by the button or by scrolling) or close the section they jumped to.
type Trail = { group: string; spots: Spot[]; index: number };

const onPageSpot = (spot: Spot) => Boolean(spot.restaurant || spot.attraction);
const chapterOf = (spot: Spot) => (spot.restaurant ? 'dining' : 'neighborhoods-explore');
const distance = (spot: Spot) => (spot.unit === 'min' ? `${spot.value} min walk` : `${spot.value} mi`);

const showOnPage = (spot: Spot) => {
  if (spot.restaurant) {
    window.dispatchEvent(new CustomEvent('corazon:open-restaurant', { detail: { name: spot.restaurant } }));
  } else if (spot.attraction) {
    window.dispatchEvent(new CustomEvent('corazon:open-attraction', { detail: { name: spot.attraction } }));
  }
};

const Chevron: React.FC<{ dir: 'left' | 'right' }> = ({ dir }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

// The bar itself. Used for the "Around here" rows (which jump further down the
// page) and for "Where we spent our weekends" (whose notes open in place).
type TrailBarProps = {
  group: string;
  items: { name: string; detail: string }[];
  index: number;
  // Element that counts as "back": once it scrolls out of view and back in, the bar goes.
  watch: string;
  // Section the guest jumped to; closing it ends the trail.
  chapter?: string;
  onStep: (index: number) => void;
  onBack: () => void;
  onDone: () => void;
};

const TrailBar: React.FC<TrailBarProps> = ({ group, items, index, watch, chapter, onStep, onBack, onDone }) => {
  const item = items[index];
  const prev = items[index - 1];
  const next = items[index + 1];

  useEffect(() => {
    const el = document.querySelector(watch);
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let left = false;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || entry.boundingClientRect.height === 0) left = true;
      else if (left) onDone();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [watch, onDone]);

  useEffect(() => {
    if (!chapter) return;
    const onState = (event: Event) => {
      const detail = (event as CustomEvent<{ id: string; open: boolean }>).detail;
      if (detail?.id === chapter && !detail.open) onDone();
    };
    window.addEventListener(CHAPTER_STATE_EVENT, onState);
    return () => window.removeEventListener(CHAPTER_STATE_EVENT, onState);
  }, [chapter, onDone]);

  return createPortal(
    <nav className="around-trail" aria-label="Places around the Hill">
      <button type="button" className="around-trail-back" onClick={onBack}>
        <Chevron dir="left" />
        <span className="around-trail-back-short">Around the Hill</span>
        <span className="around-trail-back-long">Back to Around the Hill</span>
      </button>
      <p className="around-trail-where" aria-live="polite">
        <small>{group} · {index + 1} of {items.length}</small>
        <strong>{item.name}</strong>
        {item.detail && <span className="around-trail-dist"> · {item.detail}</span>}
      </p>
      {items.length > 1 && (
        <div className="around-trail-steps">
          <button type="button" className="around-trail-prev" disabled={!prev} onClick={() => prev && onStep(index - 1)} aria-label={prev ? `Previous place: ${prev.name}` : 'Previous place'}>
            <Chevron dir="left" />
            <span className="around-trail-step-label">Previous</span>
          </button>
          <button type="button" className="around-trail-next" disabled={!next} onClick={() => next && onStep(index + 1)} aria-label={next ? `Next place: ${next.name}` : 'Next place'}>
            <span className="around-trail-step-label">{next ? `Next: ${next.name}` : 'Next'}</span>
            <Chevron dir="right" />
          </button>
        </div>
      )}
    </nav>,
    document.body,
  );
};

const scrollBackTo = (selector: string) =>
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(selector);
    (el ?? document.getElementById('the-house'))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  });

const TheHouse: React.FC = () => {
  const { ref, isVisible } = useScrollReveal(0.08);
  // The "Around here" group a guest jumped away from, and where they are in it.
  const [trail, setTrail] = useState<Trail | null>(null);
  const clearTrail = React.useCallback(() => setTrail(null), []);
  const leaveFrom = (group: Group) => (spot: Spot) => {
    const spots = group.spots.filter(onPageSpot);
    setTrail({ group: group.title, spots, index: spots.indexOf(spot) });
  };
  const trailSpot = trail ? trail.spots[trail.index] : null;
  const [active, setActive] = useState<number | null>(null);

  return (
    <section id="the-house" className="the-house the-house--around" aria-labelledby="the-house-title">
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`the-house-inner around-layout motion-reveal-group ${isVisible ? 'is-revealed' : ''}`}
      >
        <figure className="around-map motion-reveal-item" style={{ '--motion-index': 0 } as React.CSSProperties}>
          <MapArt
            pins={pins}
            active={active}
            label="Illustrated map of Capitol Hill with the house in the middle and a numbered dot for each place in the list"
          />
          <figcaption>
            <p>The star is home base: three floors and a rooftop gazebo. Point at a place to find it on the map.</p>
            <ul className="around-chips">
              {comforts.map((c) => (
                <li key={c.label}>
                  {c.href ? (
                    <a href={c.href}><span aria-hidden="true">{c.icon}</span> {c.label}</a>
                  ) : (
                    <span><span aria-hidden="true">{c.icon}</span> {c.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </figcaption>
        </figure>

        <div className="around-main">
          <header className="motion-reveal-item" style={{ '--motion-index': 1 } as React.CSSProperties}>
            <h2 id="the-house-title" className="ed-title">
              Around <em>the Hill</em>
            </h2>
            <p className="around-lede">Walk Score 96 — approximate walking times from the front door. Tap a place to see our notes or get directions; ↗ places are a short drive.</p>
          </header>

          <div className="around-groups">
            {groups.map((group, i) => (
              <div className="around-group motion-reveal-item" style={{ '--motion-index': i + 2 } as React.CSSProperties} key={group.title}>
                <h3>{group.title}</h3>
                <ul>
                  {group.spots.map((spot) => (
                    <SpotRow spot={spot} key={spot.name} onLeave={leaveFrom(group)} onPoint={setActive} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

      </div>
      {trail && trailSpot && (
        <TrailBar
          group={trail.group}
          items={trail.spots.map((s) => ({ name: s.name, detail: distance(s) }))}
          index={trail.index}
          watch={`[data-around-spot="${CSS.escape(trailSpot.name)}"]`}
          chapter={chapterOf(trailSpot)}
          onStep={(index) => {
            setTrail({ ...trail, index });
            showOnPage(trail.spots[index]);
          }}
          onBack={() => {
            window.dispatchEvent(new CustomEvent(OPEN_CHAPTER_EVENT, { detail: { id: 'the-house' } }));
            scrollBackTo(`[data-around-spot="${CSS.escape(trailSpot.name)}"]`);
            clearTrail();
          }}
          onDone={clearTrail}
        />
      )}
    </section>
  );
};

export default TheHouse;
